import { useTranslation } from 'react-i18next';
import { Container } from '@beyond-silence/ui';
import { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Profile = Record<string, any> & { id: string; display_name: string | null; avatar_url: string | null; role: string; };

interface DashboardData {
  total_published_articles: number;
  total_views: number;
  unique_visitors: number;
  top_articles: Array<{
    article_id: string; slug: string; category: string;
    estimated_read_minutes: number; views: number;
  }>;
}

export function DashboardPage() {
  const { t } = useTranslation('staff');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: prof } = await (supabase.from('profiles') as any).select('*').eq('id', user.id).single();
        if (prof) setProfile(prof);
      }

      // Use the admin dashboard RPC for proper aggregated metrics
      const { data: summary, error: sumErr } = await supabase.rpc('get_admin_dashboard_summary');
      if (sumErr) { setError(sumErr.message); setLoading(false); return; }

      // RPC returns a JSON object; cast fields safely
      const s = (summary ?? {}) as Record<string, any>;
      setData({
        total_published_articles: s.total_published_articles ?? 0,
        total_views: s.total_views ?? 0,
        unique_visitors: s.unique_visitors ?? 0,
        top_articles: (s.top_articles ?? []).map((a: any) => ({
          article_id: a.article_id,
          slug: a.slug,
          category: a.category,
          estimated_read_minutes: a.estimated_read_minutes ?? 0,
          views: a.views ?? 0,
        })),
      });
      setLoading(false);
    })();
  }, []);

  const avatarFallback = profile?.display_name
    ? profile.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  // Loading
  if (loading) {
    return (
      <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
        <div className="animate-skeleton mb-4 h-7 w-40 rounded bg-soft/40" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 md:gap-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="stat-card"><div className="animate-skeleton mb-2 h-3 w-20 rounded bg-soft/40" /><div className="animate-skeleton h-6 w-16 rounded bg-soft/40" /></div>
          ))}
        </div>
      </Container>
    );
  }

  // Error
  if (error) {
    return (
      <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 text-center">
          <p className="font-ui text-ui-sm text-primary">{error}</p>
        </div>
      </Container>
    );
  }

  if (!data) return null;

  return (
    <Container size="dashboard" className="py-4 sm:py-6 md:py-8">
      {/* Welcome header with avatar + name */}
      <div className="mb-4 flex items-center gap-4 sm:mb-6">
        {profile?.avatar_url ? (
          <img src={profile.avatar_url} alt="" loading="lazy" decoding="async" className="h-12 w-12 rounded-full object-cover ring-2 ring-soft/60 sm:h-14 sm:w-14" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary/20 font-ui text-ui-lg font-bold text-secondary sm:h-14 sm:w-14">
            {avatarFallback}
          </div>
        )}
        <div>
          <h1 className="font-ui text-ui-lg font-bold text-ink">{t('dashboard.title')}</h1>
          {profile?.display_name && (
            <p className="font-ui text-ui-sm text-ink-light">
              Welcome, <span className="font-semibold text-ink">{profile.display_name}</span>
            </p>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 md:gap-5">
        <div className="stat-card">
          <p className="font-ui text-ui-xs text-ink-light">{t('dashboard.publishedCount')}</p>
          <p className="mt-1 font-ui text-ui-lg font-bold text-ink md:text-2xl">{data.total_published_articles.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <p className="font-ui text-ui-xs text-ink-light">{t('dashboard.totalViews')}</p>
          <p className="mt-1 font-ui text-ui-lg font-bold text-ink md:text-2xl">{data.total_views.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <p className="font-ui text-ui-xs text-ink-light">Unique Visitors</p>
          <p className="mt-1 font-ui text-ui-lg font-bold text-ink md:text-2xl">{data.unique_visitors.toLocaleString()}</p>
        </div>
      </div>

      {/* Top articles */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2 md:mt-8 md:gap-6">
        <div className="stat-card">
          <h2 className="font-ui text-ui-base font-semibold text-ink">{t('dashboard.topArticles')}</h2>
          {data.top_articles.length === 0 ? (
            <p className="mt-4 font-ui text-ui-sm text-ink-light">No data yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-soft/30">
              {data.top_articles.map((a, i) => (
                <li key={a.article_id} className="flex items-center gap-3 py-2.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-highlight/40 font-ui text-ui-xs font-bold text-ink">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-ui text-ui-sm font-medium text-ink">{a.slug}</p>
                    <p className="font-ui text-ui-xs text-ink-light">{a.category} &middot; {a.estimated_read_minutes} min &middot; {(a.views ?? 0).toLocaleString()} views</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="stat-card">
          <h2 className="font-ui text-ui-base font-semibold text-ink">{t('dashboard.topAuthors')}</h2>
          <p className="mt-4 font-ui text-ui-sm text-ink-light">Author rankings coming soon.</p>
        </div>
      </div>
    </Container>
  );
}
